// render-user-profile.ts — the "user-profile" renderer: a SINGLE public user profile (user.php).
//
// Same JSON pipeline as the rest of the catalog-list bundle (PHP compiles a flat payload → the
// browser paints it), but this is NOT a filterable list: it's one user record. The endpoint
// (options/api/user_profile_inc.php) replaced the legacy page's five fetch-then-count() calls with
// cheap COUNT(*)/GROUP BY queries, so the heavy work is gone; this renderer just lays out the result.
//
// Identity, social links, and stat tiles are built from DATA (escaped via textContent / DOM attrs).
// The remaining bespoke widget from PHP helpers is the portrait carousel. Rich text ships as raw source
// strings; this renderer creates the ft-pt-mount placeholders and lets window.ftMountParsedText hydrate.

import { buildCarousel } from '../shared/build-carousel';
import type { CatalogListConfig, UserProfileMedia, UserProfileResponse, UserSocialLink, UserStat } from './types';
import { initTooltips, resolveUrl, results, setStatus, showLoader, hideLoader } from './catalog-list';

declare global {
    interface Window {
        bootstrap?: {
            Carousel?: {
                getOrCreateInstance(element: Element, options?: { ride?: boolean }): unknown;
            };
        };
        ftMountParsedText?: (root?: Element | Document) => void;
    }
}

// A labelled identity row: <bold label> … <value badge>.
function metaRow(label: string, value: string, badgeClass = ''): HTMLElement {
    const li = document.createElement('div');
    li.className = 'ft-profile-meta-row';

    const k = document.createElement('span');
    k.className = 'ft-profile-meta-label';
    k.textContent = label;
    li.appendChild(k);

    const v = document.createElement('span');
    v.className = 'ft-profile-meta-value' + (badgeClass ? ' ' + badgeClass : '');
    v.textContent = value || '—';
    li.appendChild(v);

    return li;
}

// One stat tile: big number, a label under it, and an optional "(percent)" sub-line.
function statTile(value: string, label: string, sub: string | null, accent: boolean): HTMLElement {
    const tile = document.createElement('div');
    tile.className = 'ft-stat-tile' + (accent ? ' ft-stat-tile--accent' : '');

    const num = document.createElement('div');
    num.className = 'ft-stat-value';
    num.textContent = value;
    tile.appendChild(num);

    const lab = document.createElement('div');
    lab.className = 'ft-stat-label';
    lab.textContent = label;
    tile.appendChild(lab);

    if (sub) {
        const s = document.createElement('div');
        s.className = 'ft-stat-sub';
        s.textContent = sub;
        tile.appendChild(s);
    }
    return tile;
}

// A section card with an icon + title header and a body the caller fills.
function sectionCard(iconId: string, title: string): { card: HTMLElement; body: HTMLElement } {
    const card = document.createElement('div');
    card.className = 'ft-profile-card';

    const header = document.createElement('div');
    header.className = 'ft-profile-card-header';
    const icon = document.createElement('span');
    icon.className = 'ft-profile-card-icon';
    icon.innerHTML = iconMap(iconId);
    header.appendChild(icon);
    const h = document.createElement('h3');
    h.className = 'ft-profile-card-title';
    h.textContent = title;
    header.appendChild(h);
    card.appendChild(header);

    const body = document.createElement('div');
    body.className = 'ft-profile-card-body';
    card.appendChild(body);

    return { card, body };
}

function socialLink(link: UserSocialLink): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'p-2';

    const a = document.createElement('a');
    a.href = link.url;
    a.className = 'd-inline-flex align-items-center';
    a.setAttribute('aria-label', link.label || link.platform);
    a.rel = 'noopener noreferrer';
    a.target = '_blank';
    a.innerHTML = iconMap(link.platform);
    wrap.appendChild(a);

    return wrap;
}

function parseTextMount(text: string): HTMLElement {
    const mount = document.createElement('span');
    mount.className = 'ft-pt-mount';

    const blob = document.createElement('script');
    blob.type = 'application/json';
    blob.className = 'ft-pt-blob';
    blob.textContent = JSON.stringify({ text });
    mount.appendChild(blob);

    return mount;
}

function initBootstrapCarousel(host: HTMLElement): void {
    try {
        const bs = window.bootstrap;
        if (!bs || !bs.Carousel) return;
        const el = host.querySelector('.carousel');
        if (el) bs.Carousel.getOrCreateInstance(el, { ride: false });
    } catch (e) {
        // The delegated Bootstrap click handlers still cover prev/next when an instance is unavailable.
    }
}

function profileCarousel(media: UserProfileMedia): HTMLElement {
    const mount = document.createElement('div');
    mount.className = 'ft-carousel-mount';
    mount.innerHTML = buildCarousel({
        domId: `${media.domId}_profile`,
        itemName: media.name,
        pics: Array.isArray(media.pictures) ? media.pictures : [],
        videos: Array.isArray(media.videos) ? media.videos : [],
        height: typeof media.height === 'number' ? media.height : undefined,
        activePicId: media.activePicId ?? null,
        setPic: null,
    });
    initBootstrapCarousel(mount);
    return mount;
}

function appendRichText(body: HTMLElement, title: string, text: string, extraHeadingClass = ''): boolean {
    if (!text) return false;

    const h = document.createElement('h4');
    h.className = `ft-profile-subhead${extraHeadingClass ? ' ' + extraHeadingClass : ''}`;
    h.textContent = title;
    body.appendChild(h);

    const wrap = document.createElement('div');
    wrap.className = 'ft-profile-richtext';
    wrap.appendChild(parseTextMount(text));
    body.appendChild(wrap);

    return true;
}

function build(container: HTMLElement, data: UserProfileResponse): void {
    container.innerHTML = '';

    // One row, two columns. LEFT column stacks: user info → GM info (if any) → player info; the RIGHT
    // column holds the (usually tall) about / GM-style text, so the left column's space is never wasted.
    const mainRow = document.createElement('div');
    mainRow.className = 'row g-4 g-lg-5 ft-profile';
    const leftCol = document.createElement('div');
    leftCol.className = 'col-lg-5 ft-profile-stack';
    const rightCol = document.createElement('div');
    rightCol.className = 'col-lg-7';
    mainRow.appendChild(leftCol);
    mainRow.appendChild(rightCol);

    // ── Identity card (portrait + meta + socials) ───────────────────────────────────────────────
    {
        const { card, body } = sectionCard('user', data.username || 'Profile');

        const portrait = document.createElement('div');
        portrait.className = 'ft-profile-portrait';
        if (data.media) {
            portrait.appendChild(profileCarousel(data.media));
        } else {
            const ph = document.createElement('div');
            ph.className = 'ft-profile-portrait-empty';
            ph.innerHTML = iconMap('user', 64, 64);
            const cap = document.createElement('span');
            cap.textContent = 'No profile picture';
            ph.appendChild(cap);
            portrait.appendChild(ph);
        }
        body.appendChild(portrait);

        const meta = document.createElement('div');
        meta.className = 'ft-profile-meta';
        meta.appendChild(metaRow('Account Type', data.identity.account_type));
        meta.appendChild(metaRow('Years of TTRPG Experience', data.identity.years_ttrpg, 'badge bg-primary'));
        meta.appendChild(metaRow('Years of GM Experience', data.identity.years_gm, 'badge bg-secondary'));
        meta.appendChild(metaRow('Timezone', data.identity.timezone_label));
        meta.appendChild(metaRow('Timezone Difference', data.identity.tz_diff_label, 'badge bg-warning text-dark'));
        body.appendChild(meta);

        if (Array.isArray(data.socials) && data.socials.length > 0) {
            const socials = document.createElement('div');
            socials.className = 'ft-profile-socials d-flex align-items-center justify-content-start flex-wrap';
            for (const link of data.socials) {
                socials.appendChild(socialLink(link));
            }
            body.appendChild(socials);
        }

        leftCol.appendChild(card);
    }

    // ── About / GM Style card ───────────────────────────────────────────────────────────────────
    {
        const { card, body } = sectionCard('about', 'About');

        let hasContent = false;
        hasContent = appendRichText(body, 'About', data.about_text || '') || hasContent;
        hasContent = appendRichText(body, 'GM Style', data.gm_style_text || '', 'mt-4') || hasContent;
        if (!hasContent) {
            const muted = document.createElement('p');
            muted.className = 'text-muted m-0';
            muted.textContent = 'This adventurer has not written an introduction yet.';
            body.appendChild(muted);
        }

        rightCol.appendChild(card);
    }

    // ── GM Information stats (only when the user actually GMs) ────────────────────────────────────
    {
        const gs = data.gm_stats;
        if (gs.games_ran > 0 || gs.campaigns_running > 0 || gs.sessions_planned > 0) {
            const { card, body } = sectionCard('gm', 'GM Information');
            const statGrid = document.createElement('div');
            statGrid.className = 'ft-stat-grid';
            statGrid.appendChild(statTile(String(gs.games_ran), 'Games Ran', null, true));
            statGrid.appendChild(statTile(String(gs.campaigns_running), 'Campaigns Running', null, false));
            statGrid.appendChild(statTile(String(gs.sessions_planned), 'Sessions Planned', null, false));
            body.appendChild(statGrid);
            leftCol.appendChild(card);
        }
    }

    // ── Player Information stats ─────────────────────────────────────────────────────────────────
    {
        const { card, body } = sectionCard('player', 'Player Information');
        const ps = data.player_stats;
        const statGrid = document.createElement('div');
        statGrid.className = 'ft-stat-grid';
        const sub = (s: UserStat) => `${s.percent} of games`;
        statGrid.appendChild(statTile(String(ps.games_played), 'Games Played', null, true));
        statGrid.appendChild(statTile(String(ps.attendance_score), 'Attendance Score', null, true));
        statGrid.appendChild(statTile(String(ps.no_show.count), 'No Shows', sub(ps.no_show), false));
        statGrid.appendChild(statTile(String(ps.late.count), 'Showed Up Late', sub(ps.late), false));
        statGrid.appendChild(statTile(String(ps.left_early.count), 'Left Early', sub(ps.left_early), false));
        statGrid.appendChild(statTile(String(ps.skipped.count), 'Games Skipped', sub(ps.skipped), false));
        statGrid.appendChild(statTile(String(ps.late_cancel.count), 'Late Cancels', sub(ps.late_cancel), false));
        body.appendChild(statGrid);
        leftCol.appendChild(card);
    }

    container.appendChild(mainRow);

    // Hydrate injected widgets: parse-text mounts (about / GM style), then icon placeholders + tooltips
    // (socials icon_ph + the section-header icons we built via iconMap). initTooltips runs ftMountIcons.
    if (typeof window.ftMountParsedText === 'function') {
        try { window.ftMountParsedText(container); } catch { /* footer pass covers it */ }
    }
    initTooltips(container);
}

export function mountUserProfile(root: HTMLElement, config: CatalogListConfig): void {
    const container = results(root);
    if (!container) {
        console.error('[user-profile] mount missing [data-ft-catalog-results]', root);
        return;
    }

    void (async () => {
        const loadingTimer = setTimeout(() => showLoader(root, config), 250);
        try {
            const res = await fetch(resolveUrl(config.endpoint), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: '{}',
            });
            // Read as text first so a stray PHP warning / HTML 500 surfaces a useful message rather than
            // an opaque JSON.parse error (same guard as render-my-campaigns.ts).
            const raw = await res.text();
            let data: UserProfileResponse;
            try {
                data = JSON.parse(raw) as UserProfileResponse;
            } catch {
                throw new Error(`Non-JSON response (HTTP ${res.status}): ${raw.slice(0, 300)}`);
            }
            if (!res.ok || data.error) {
                throw new Error(data.error || `HTTP ${res.status}`);
            }
            clearTimeout(loadingTimer);
            hideLoader(root);
            setStatus(root, '');
            build(container, data);
        } catch (e) {
            clearTimeout(loadingTimer);
            hideLoader(root);
            console.error('[user-profile] fetch/render failed', e);
            setStatus(root, '');
            container.innerHTML = '';
            const msg = document.createElement('div');
            msg.className = 'col-12 text-center text-muted py-4';
            msg.textContent = 'Could not load this profile. Please try again.';
            container.appendChild(msg);
        }
    })();
}
